import React, { useEffect, useMemo, useRef, useState } from 'react';
import resolveAssetUrl from '../utils/assets';
import { getTeamIdFromPlayer } from '../utils/player';
import ProgressBar from './ui/ProgressBar';
import './css/Finalfrage.css';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Eigenständige Finalfrage-Bühne (getrennt von Decoder/Quizpoker).
// Team-Karten leben im bestehenden Teamcontainer (TeamSheet), NICHT hier.
function FinaleFinalfrageStage({
    finale,
    teams = [],
    allPlayers = [],
    yourTeamId = null,
    onSetWager,
    onLockWager,
    onSelectAnswer,
}) {
    const live = finale?.live || null;
    const question = finale?.question || null;
    const phase = live?.phase || null;
    const isBeamer = yourTeamId == null;

    const [wagerInput, setWagerInput] = useState('');
    const wagerSyncedRef = useRef(false);

    const ownTeam = useMemo(
        () => teams.find((t) => String(t.id) === String(yourTeamId)) || null,
        [teams, yourTeamId]
    );
    const ownEntry = live?.perTeam?.[String(yourTeamId)] || null;
    const maxWager = Math.max(0, Number(ownTeam?.punkte) || 0);

    // Eingabefeld EINMAL beim Betreten der wager-Phase vorbelegen – danach nicht mehr
    // ueberschreiben (sonst springt die Zahl beim Tippen). Validiert wird erst beim Bestaetigen.
    useEffect(() => {
        if (phase !== 'wager') {
            wagerSyncedRef.current = false;
            return;
        }
        if (ownEntry && !ownEntry.wagerLocked && !wagerSyncedRef.current) {
            setWagerInput(ownEntry.wager ? String(ownEntry.wager) : '');
            wagerSyncedRef.current = true;
        }
    }, [phase, ownEntry]);

    const revealedCount = live?.revealedAnswerCount ?? 0;
    const answers = Array.isArray(question?.answers) ? question.answers : [];
    const correctIndex = Number.isInteger(question?.correctIndex) ? question.correctIndex : -1;
    const solutionRevealed = !!live?.solutionRevealed;

    const showCategory = !!question?.category && !!live?.categoryVisible && phase !== 'resolved';
    const showQuestion = !!question?.text && ['question', 'countdown', 'locked', 'revealPicks', 'revealSolution'].includes(phase);
    const categoryCorner = showQuestion && !!live?.categoryVisible;

    const countdownRunning =
        phase === 'countdown' &&
        Number.isFinite(live?.countdownEndsAt) &&
        live.countdownEndsAt > Date.now();

    const countdownSeconds = finale?.config?.countdownSeconds || 20;

    // Bild (optional)
    const attachments = Array.isArray(question?.attachments) ? question.attachments : [];
    const image = attachments.find((a) => (a?.mime || '').startsWith('image/')) || attachments[0] || null;

    // Farbpunkte je Antwort: welche Teams haben diesen Index gewählt (aufgedeckt)?
    const picksByAnswer = useMemo(() => {
        const map = {};
        const per = live?.perTeam || {};
        for (const [teamId, entry] of Object.entries(per)) {
            const visible = entry.revealed || solutionRevealed;
            if (!visible || entry.pick == null) continue;
            (map[entry.pick] = map[entry.pick] || []).push(teamId);
        }
        return map;
    }, [live, solutionRevealed]);

    const teamColor = (teamId) => live?.teamColors?.[String(teamId)] || 'var(--muted)';
    const teamName = (teamId) => teams.find((t) => String(t.id) === String(teamId))?.name || `Team ${teamId}`;

    const teamMemberNames = (teamId) => {
        const t = teams.find((x) => String(x.id) === String(teamId));
        if (t?.spieler && t.spieler.length) return t.spieler.map((p) => p.name).filter(Boolean);
        return allPlayers
            .filter((p) => String(getTeamIdFromPlayer(p)) === String(teamId))
            .map((p) => p.name)
            .filter(Boolean);
    };

    // ---- Schluss-Ansicht ----
    if (phase === 'resolved') {
        const result = live?.result || {};
        const winners = new Set((result.winnerTeamIds || []).map(String));
        const rows = [...teams]
            .map((t) => ({
                id: String(t.id),
                name: t.name,
                score: Number(t.punkte) || 0,
                members: teamMemberNames(t.id),
            }))
            .sort((a, b) => b.score - a.score);
        const winnerRows = rows.filter((r) => winners.has(r.id));
        const restRows = rows.filter((r) => !winners.has(r.id));

        const Tile = (r) => (
            <div
                key={r.id}
                className={`ff-resolved-tile${winners.has(r.id) ? ' ff-winner' : ''}`}
                style={{ borderTopColor: teamColor(r.id) }}
            >
                <span className="ff-resolved-name">{r.name}</span>
                <span className="ff-resolved-score">{r.score}</span>
                {r.members.length > 0 && (
                    <span className="ff-resolved-members">{r.members.join(' · ')}</span>
                )}
            </div>
        );

        return (
            <div className="ff-stage ff-stage-resolved">
                {winnerRows.length > 0 && (
                    <div className="ff-resolved-winners-wrap">
                        <span className="ff-besserwisser">BESSERWISSER</span>
                        <div className="ff-resolved-grid ff-resolved-winners">
                            {winnerRows.map(Tile)}
                        </div>
                    </div>
                )}
                {restRows.length > 0 && (
                    <div className="ff-resolved-grid ff-resolved-rest">
                        {restRows.map(Tile)}
                    </div>
                )}
            </div>
        );
    }

    const renderOwnWager = () => {
        if (isBeamer || phase !== 'wager' || !ownEntry) return null;
        if (ownEntry.wagerLocked) {
            return (
                <div className="ff-own-wager ff-own-wager-locked">
                    Einsatz bestätigt: <strong>{ownEntry.wager}</strong>
                </div>
            );
        }
        const confirmWager = () => {
            const v = Math.max(0, Math.floor(Number(wagerInput) || 0));
            setWagerInput(String(v));
            // Wert setzen UND sperren in einem Event (verhindert die set/lock-Race,
            // bei der der Einsatz auf 0 hängen bleibt).
            onSetWager && onSetWager(v, true);
        };
        return (
            <div className="ff-own-wager">
                <label className="ff-own-wager-label">
                    Dein Einsatz{maxWager > 0 ? ` (aktuell ${maxWager} Punkte)` : ''}
                </label>
                <div className="ff-own-wager-row">
                    <input
                        type="number"
                        inputMode="numeric"
                        value={wagerInput}
                        onChange={(e) => setWagerInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmWager(); }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={confirmWager}>
                        Einsatz bestätigen
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="ff-stage">
            {/* Kopfzeile: Label links (erst ab Kategorie-Anzeige) · Kategorie-Ecke rechts */}
            <div className="ff-stage-header">
                {!!live?.categoryVisible && <span className="ff-stage-tag">Finalfrage</span>}
                {categoryCorner && question?.category && (
                    <span className="ff-cat-corner">{question.category}</span>
                )}
            </div>

            {(countdownRunning || phase === 'countdown') && (
                <div className="ff-timer-sticky">
                    <ProgressBar
                        durationMs={countdownSeconds * 1000}
                        endsAtMs={live?.countdownEndsAt || Date.now()}
                    />
                </div>
            )}

            <div className="ff-stage-main">
                {/* Neutraler Splash, solange nichts freigegeben ist */}
                {!showCategory && !showQuestion && phase !== 'resolved' && (
                    <div className="ff-category-splash">
                        <span className="ff-category-value ff-splash-title">FINALFRAGE</span>
                    </div>
                )}

                {/* Kategorie groß (Phase 1–2) */}
                {showCategory && !showQuestion && (
                    <div className="ff-category-splash">
                        <span className="ff-category-label">Kategorie</span>
                        <span className="ff-category-value">{question.category}</span>
                    </div>
                )}

                {showQuestion && <div className="ff-question">{question.text}</div>}

                {image && showQuestion && (
                    <div className="ff-image">
                        <img src={resolveAssetUrl(image.url)} alt="" />
                    </div>
                )}

                {showQuestion && answers.length > 0 && (
                    <div className="ff-answers">
                        {answers.slice(0, revealedCount).map((text, idx) => {
                            const isCorrect = solutionRevealed && idx === correctIndex;
                            const ownPicked = ownEntry && ownEntry.pick === idx;
                            const dots = picksByAnswer[idx] || [];
                            const clickable = !isBeamer && countdownRunning;
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    className={`ff-answer${isCorrect ? ' ff-answer-correct' : ''}${ownPicked ? ' ff-answer-own' : ''}`}
                                    style={ownPicked ? { '--own': teamColor(yourTeamId) } : undefined}
                                    disabled={!clickable}
                                    onClick={() => clickable && onSelectAnswer && onSelectAnswer(idx)}
                                >
                                    <span className="ff-answer-key">{LETTERS[idx] || idx + 1}</span>
                                    <span className="ff-answer-text">{text}</span>
                                    {dots.length > 0 && (
                                        <span className="ff-answer-dots">
                                            {dots.map((tid) => (
                                                <i
                                                    key={tid}
                                                    style={{ background: teamColor(tid) }}
                                                    title={teamName(tid)}
                                                />
                                            ))}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}

                {renderOwnWager()}
            </div>
        </div>
    );
}

export default FinaleFinalfrageStage;
